import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  Breadcrumb,
  InfoLabel,
  Modal,
  ProgressIndicator,
  TextArea
} from '@/components/atoms';
import {
  AccordionItem,
  ImageUploadField,
  PageHeader,
  SaveCancelFooter,
  TextInputField
} from '@/components/molecules';
import {MediaPreviewWrapper, PageContainer} from '@/components/organisms';
import {MEDIA_PREVIEW_CARD} from '@/components/organisms/MediaPreview/mediaPreviewConfig';
import {useAppSelector, useAppDispatch} from '@/store';
import {
  setSalesTargetDisplayContent,
  setSalesTargetDisplayContentMedia,
  resetSalesTargetDisplayContentMedia
} from '@/store/slices/salesTargetSlice';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {ROUTES} from '@/utils/constants/routesConstants';
import {MODAL_TYPE, FONT_SIZE} from '@/utils/constants/globalStyle';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {getStepConfig} from '@/utils/salesTargetStepConfig';
import {TARGET_TYPES, IMAGETYPE} from '@/utils/constants/saleTargetConstants';
import {logger} from '@/helpers/logger';

// --- Constants ---
const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 300;
const IMAGE_TYPES = ['image/webp', 'image/jpeg', 'image/png'];

const TARGET_IMAGE_DIMENSIONS = {width: 72, height: 218};
const TARGET_IMAGE_MAX_SIZE_BYTES = 70 * 1024; // 70 KB
const TARGET_IMAGE_DESCRIPTION = `Required: WebP, JPG, PNG \u2022 ${TARGET_IMAGE_DIMENSIONS.width} \u00d7 ${TARGET_IMAGE_DIMENSIONS.height} px RGB \u2022 Max. ${TARGET_IMAGE_MAX_SIZE_BYTES / 1024} KB`;

const STRING_SCHEMA = {type: 'string'};

const REQUIRED_RULE = {type: 'required', msg: ' is required'};
const maxLengthRule = (limit) => ({
  type: 'maxLength',
  limit,
  msg: ` must be less than or equal to ${limit} characters`
});

const titleUiSchema = {
  label: 'Title*',
  placeholder: 'Title',
  isDark: true,
  tooltipText: `Limit: ${TITLE_MAX_LENGTH} characters, 2 lines maximum. Spaces are counted`,
  validationRules: [REQUIRED_RULE, maxLengthRule(TITLE_MAX_LENGTH)]
};

const descriptionUiSchema = {
  label: 'Description*',
  placeholder: 'Description',
  isDark: true,
  showToolbar: false,
  rows: 5,
  maxLength: DESCRIPTION_MAX_LENGTH,
  tooltipText: `Limit ${DESCRIPTION_MAX_LENGTH} characters`,
  validationRules: [REQUIRED_RULE, maxLengthRule(DESCRIPTION_MAX_LENGTH)]
};

// --- Helpers ---
const updateLanguageField = (
  existingMap,
  langCode,
  field,
  value,
  hasError
) => ({
  ...existingMap,
  [langCode]: {
    ...existingMap?.[langCode],
    [field]: value,
    [`${field}Valid`]: hasError
  }
});

// --- Sub-components ---

/** Field config for the display information grid */
const DISPLAY_FIELDS = [
  {key: 'title', type: 'input', uiSchema: titleUiSchema},
  {key: 'description', type: 'textarea', uiSchema: descriptionUiSchema}
];

/** Display Information section with language columns */
const DisplayInformationSection = ({languages, content, onContentChange}) => {
  return (
    <div>
      <h3 className='mb-1 text-sm font-bold'>Target Type</h3>
      <p className='mb-6 text-sm font-semibold'>Rack Challenge</p>

      <div className='grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 lg:grid-cols-3'>
        {languages.map((lang) => (
          <div key={lang.code} className='min-w-0'>
            <p className='mb-4 text-sm font-bold text-white'>{lang.title}</p>
            {DISPLAY_FIELDS.map((field) => {
              const data = content?.[lang.code] || {};
              const commonProps = {
                value: data[field.key] || '',
                label: field.uiSchema.label,
                updateValue: (val, hasError) =>
                  onContentChange(lang.code, field.key, val, hasError)
              };

              return (
                <div key={`${field.key}-${lang.code}`} className='mb-4'>
                  {field.type === 'input' ? (
                    <TextInputField
                      {...commonProps}
                      schema={STRING_SCHEMA}
                      uiSchema={field.uiSchema}
                      visible
                    />
                  ) : (
                    <TextArea
                      {...commonProps}
                      uiSchema={field.uiSchema}
                      trim={false}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Page Component ---
export const RackChallengeDisplayContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const salesTarget = useAppSelector((state) => state.salesTarget);
  const {handleDraft} = useSalesTargets(false);

  const isEdit = location.state?.isEdit ?? false;
  const disableDraft = location.state?.disableDraft ?? false;
  const salesTargetIdFromState = location.state?.salesTargetId;

  // Forwarded so the Review page can tell an edit-of-published record apart from a new one and PUT instead of POST
  const getNavigationState = () => ({
    isEdit,
    disableDraft,
    editPublishedBeforeStart: disableDraft,
    salesTargetId: salesTargetIdFromState || salesTarget?.id
  });

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  // --- Media state ---
  const [targetImage, setTargetImage] = useState(null);

  // Rehydrate the previously uploaded image when navigating back to this page
  useEffect(() => {
    const media = salesTarget?.displayContentMedia;
    const imageUri = media?.imageUri || media?.imageUrl;
    if (imageUri) {
      setTargetImage({
        fileName: media?.title,
        imageUri: media?.imageUri,
        imageUrl: media?.imageUrl
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = useCallback(
    (image) => {
      setTargetImage(image);
      dispatch(
        setSalesTargetDisplayContentMedia({
          title: image?.fileName || '',
          imageId: IMAGETYPE.Foreground,
          imageUri: image?.imageUrl || image?.imageUri || '',
          imageUrl: image?.imageUrl || '',
          optIn: false
        })
      );
    },
    [dispatch]
  );

  const handleImageDelete = useCallback(() => {
    setTargetImage(null);
    dispatch(resetSalesTargetDisplayContentMedia());
  }, [dispatch]);

  // --- Display content state ---
  const [displayContent, setDisplayContent] = useState({});

  // Rehydrate previously entered title/description when navigating back to this page
  useEffect(() => {
    if (!languages?.length) return;
    const hydrated = {};
    languages.forEach((lang) => {
      hydrated[lang.code] = {...salesTarget?.displayContent?.[lang.code]};
    });
    setDisplayContent(hydrated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  // --- UI state ---
  const [isCancel, setIsCancel] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(true);

  // --- Content handlers ---
  const handleContentChange = useCallback(
    (langCode, field, value, hasError) => {
      setDisplayContent((prev) =>
        updateLanguageField(prev, langCode, field, value, hasError)
      );
    },
    []
  );

  // Build displayContentFormData from local state for the draft API
  const buildDisplayContentFormData = useCallback(() => {
    const formData = {};
    languages.forEach((lang) => {
      const data = displayContent?.[lang.code] || {};
      formData[lang.code] = {
        targetType: salesTarget?.targetType,
        title: data.title || '',
        description: data.description || ''
      };
    });
    return formData;
  }, [languages, displayContent, salesTarget?.targetType]);

  // --- Navigation handlers ---
  const handleCancel = () => setIsCancel(!isCancel);

  const onConfirmCancel = () => {
    setIsCancel(false);
    navigate(ROUTES.SALES_TARGETS);
  };

  const handleDraftAction = async () => {
    try {
      const displayContentFormData = buildDisplayContentFormData();
      const response = await handleDraft(
        salesTarget,
        salesTarget,
        false,
        'displayContent',
        {displayContentFormData}
      );
      if (response?.success) {
        navigate(ROUTES.SALES_TARGETS);
      }
    } catch (error) {
      logger.error('Error saving rack challenge display draft:', error);
    }
  };

  const handleNextAction = () => {
    // Review screen reads displayContent from redux, so persist it before navigating
    dispatch(setSalesTargetDisplayContent(buildDisplayContentFormData()));
    navigate(ROUTES.SALES_TARGET_REVIEW, {state: getNavigationState()});
  };

  // --- Validation ---
  const getFormError = () => {
    if (!targetImage) return true;

    for (const lang of languages) {
      const content = displayContent?.[lang.code];
      if (!content?.title || content?.title.trim() === '') return true;
      if (content?.titleValid === true) return true;
      if (!content?.description || content?.description.trim() === '')
        return true;
      if (content?.descriptionValid === true) return true;
    }
    return false;
  };

  const stepConfig = getStepConfig(TARGET_TYPES.RackChallenge);
  const previewImage = targetImage?.imageUri || targetImage?.imageUrl || '';

  const handleStepClick = (step) => {
    const stepRouteMap = {
      1: ROUTES.SALES_TARGET_ADD,
      3: ROUTES.SALES_TARGET_REVIEW
    };
    const route = stepRouteMap[step];
    if (route) {
      navigate(route, {state: getNavigationState()});
    }
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {href: ROUTES.HOME, label: ''},
            {href: ROUTES.SALES_TARGETS, label: 'Loyalty Campaigns'},
            {href: ROUTES.SALES_TARGETS, label: 'Add Sales Target'}
          ]
        }}
        id='rcdc_bradcrumb'
      />

      <PageHeader title='Add Sales Target' />

      <ProgressIndicator
        currentStep={2}
        completed={false}
        orientation='horizontal'
        steps={stepConfig.steps}
        titles={stepConfig.titles}
        className='mb-6'
        onStepClick={handleStepClick}
      />
      <br />

      {/* Display Image Accordion */}
      <PageContainer data-testid='rack-challenge-media'>
        <div className='rack-challenge-media-accordion'>
          <AccordionItem
            isDark
            expanded={imageExpanded}
            index={0}
            label='Display Image'
            onUpdate={() => setImageExpanded((prev) => !prev)}>
            <div className='w-full pt-4'>
              <h3 className='mb-1 text-base font-bold'>Target Image*</h3>

              <div
                className='mb-4 grid gap-6 rounded-lg border border-[color:var(--color-border-default)] p-4 md:grid-cols-2'
                data-testid='target-image-row'>
                <div>
                  <ImageUploadField
                    label='Upload file'
                    tooltipText={TARGET_IMAGE_DESCRIPTION}
                    description={TARGET_IMAGE_DESCRIPTION}
                    imageUri={previewImage}
                    acceptedTypes={IMAGE_TYPES}
                    dimensions={TARGET_IMAGE_DIMENSIONS}
                    validateDimensions={false}
                    validateAspectRatio={false}
                    maxSizeBytes={TARGET_IMAGE_MAX_SIZE_BYTES}
                    onChange={handleImageChange}
                    onDelete={handleImageDelete}
                    dataTestId='target-image-upload'
                    containerClassName='w-full rounded-lg border border-[color:var(--color-border-default)] p-3'
                  />
                </div>
                <div>
                  <p className='mb-3 text-sm font-bold'>Image Preview in App</p>
                  <MediaPreviewWrapper
                    types={[MEDIA_PREVIEW_CARD]}
                    scale={0.55}
                    content={[{image: previewImage || undefined}]}
                  />
                </div>
              </div>

              <InfoLabel title='No text in the image' />
            </div>
          </AccordionItem>
        </div>
      </PageContainer>

      {/* Display Information */}
      <PageContainer data-testid='rack-challenge-display-content'>
        <PageHeader title='Display Information' fontSize={FONT_SIZE.LG} />
        <DisplayInformationSection
          languages={languages}
          content={displayContent}
          onContentChange={handleContentChange}
        />
      </PageContainer>

      {/* Cancel Modal */}
      {isCancel && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text='Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={onConfirmCancel}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={handleCancel}
          footerProps={{size: 'medium', type: 'primary'}}
          isDark
        />
      )}

      {/* Footer */}
      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
        disableDraft={disableDraft}
      />
    </>
  );
};
